/**
 * EdgeDocs Editor Store
 * Manages editor-specific state for the EdgeDocs module
 */

import { create } from 'zustand';
import { DocumentDetail, DocumentListItem, EditorDocumentState } from '../../../shared/document/types';

interface EditorStoreState {
  // Active document
  activeDocument: DocumentDetail | DocumentListItem | null;
  isEditorOpen: boolean;
  
  // Editor state
  editorState: EditorDocumentState;
  
  // UI state
  showToolbar: boolean;
  showSidebar: boolean;
  panelMode: boolean;
  readOnly: boolean;
  
  // Actions
  openEditor: (document: DocumentDetail | DocumentListItem) => void;
  closeEditor: () => void;
  setActiveDocument: (document: DocumentDetail | DocumentListItem | null) => void;
  updateEditorState: (updates: Partial<EditorDocumentState>) => void;
  resetEditorState: () => void;
  setPanelMode: (enabled: boolean) => void;
  setReadOnly: (enabled: boolean) => void;
  toggleToolbar: () => void;
  toggleSidebar: () => void;
  
  // Document operations
  loadDocumentContent: (documentId: string) => Promise<void>;
  saveDocumentContent: (content: string, metadata?: Record<string, any>) => Promise<void>;
}

const initialEditorState: EditorDocumentState = {
  content: '',
  wordCount: 0,
  characterCount: 0,
  pageCount: 1,
  lastSaved: null,
  hasUnsavedChanges: false,
  isProcessing: false,
  selectedTemplate: null,
  documentMetadata: {},
};

export const useEditorStore = create<EditorStoreState>((set, get) => ({
  // Initial state
  activeDocument: null,
  isEditorOpen: false,
  editorState: initialEditorState,
  showToolbar: true,
  showSidebar: false,
  panelMode: false,
  readOnly: false,
  
  // Actions
  openEditor: (document) => {
    set({
      activeDocument: document,
      isEditorOpen: true,
      panelMode: false,
    });
  },
  
  closeEditor: () => {
    set({
      isEditorOpen: false,
      activeDocument: null,
      editorState: initialEditorState,
    });
  },
  
  setActiveDocument: (document) => {
    console.log('🔍 [editorStore] setActiveDocument called with:', document?.id, document?.title);
    set({ activeDocument: document });
  },
  
  updateEditorState: (updates) => {
    console.log('🔍 [editorStore] updateEditorState called with:', updates);
    set((state) => ({
      editorState: { ...state.editorState, ...updates },
    }));
  },
  
  resetEditorState: () => {
    console.log('🔍 [editorStore] resetEditorState called');
    set({ editorState: initialEditorState });
  },
  
  setPanelMode: (enabled) => {
    set({ panelMode: enabled });
  },
  
  setReadOnly: (enabled) => {
    set({ readOnly: enabled });
  },
  
  toggleToolbar: () => {
    set((state) => ({ showToolbar: !state.showToolbar }));
  },
  
  toggleSidebar: () => {
    set((state) => ({ showSidebar: !state.showSidebar }));
  },
  
  // Document operations
  loadDocumentContent: async (_documentId) => {
    console.log('🔍 [editorStore] loadDocumentContent called for:', _documentId);
    // For new documents, we don't need to load content from API
    // The content will be initialized in the LexicalDocumentEditor
    set(() => ({
      editorState: { ...initialEditorState, isProcessing: false },
    }));
  },
  
  saveDocumentContent: async (content, metadata) => {
    const { activeDocument } = get();
    if (!activeDocument) return;
    
    set(() => ({
      editorState: { ...initialEditorState, isProcessing: true },
    }));
    
    try {
      // TODO: Implement actual save to backend
      // For now, just update the local state
      console.log('Saving document content:', { content, metadata });
      
      set(() => ({
        editorState: {
          ...initialEditorState,
          content,
          lastSaved: new Date(),
          hasUnsavedChanges: false,
          isProcessing: false,
        },
      }));
    } catch (error) {
      console.error('Failed to save document content:', error);
      set(() => ({
        editorState: { ...initialEditorState, isProcessing: false },
      }));
    }
  },
})); 